import { ItemImage } from '@components/CatalogElements';
import { OrganisationAvatar } from '@components/ImageAvatar';
import { SkupremeModal } from '@components/Modal';
import { HoverBorderButton, StyledButton } from '@elements/Button';
import { BoldText, ColorText, DangerText, MuteText } from '@elements/Common';
import { CenterDropdownStyle } from '@elements/Dropdown';
import { ColoredPill } from '@elements/Pills';
import { useAllocationsLocation } from '@hooks/allocation';
import { Warning, X } from '@phosphor-icons/react';
import { AllocationService } from '@utility/services';
import { theme } from '@utility/theme';
import { ToastMessage } from '@utility/toast';
import type { Dispatch, SetStateAction } from 'react';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

export type AllocationSelection = {
  [key: string]: {
    selected: boolean;
    productId: string;
    image: string;
    name: string;
    sku: string;
    location: string;
    zone: string;
    rack: string;
    color: string;
    orderId: string;
    requiresTote: boolean;
    id: string;
    customerName: string;
    customerImage: string;
    customerId: string;
  };
};

type ChangeLocationPopupProps = {
  open: boolean;
  onClose: () => void;
  allocationSelection: AllocationSelection;
  setAllocationSelection: Dispatch<SetStateAction<AllocationSelection>>;
  refresh: () => void;
  warehouseId: string;
};

const AllocationItemContainer = styled.div`
  padding: 10px;
  background-color: ${theme.colors.box};
  border-radius: 5px;
  .cancel-icon {
    color: ${theme.colors.grey};
    &:hover {
      color: ${theme.colors.primary};
    }
  }
`;

function AllocationItem({
  allocation,
  onRemove,
}: {
  allocation: AllocationSelection[keyof AllocationSelection];
  onRemove: (allocationId: string) => void;
}) {
  return (
    <AllocationItemContainer className="row align-items-center">
      <div className="col-5">
        <div>
          <MuteText>Order ID:</MuteText>
          <span className="ml-1">{allocation.orderId}</span>
        </div>
        <div>
          <MuteText>Location:</MuteText>
          <ColorText color={allocation.color} className="ml-1">
            {allocation.location}
          </ColorText>
        </div>
      </div>
      <div className="col-5">
        <div>
          <MuteText>Zone:</MuteText>
          <ColorText color={allocation.color} className="ml-1">
            {allocation.zone}
          </ColorText>
        </div>
        <div>
          <MuteText>Rack:</MuteText>
          <ColorText color={allocation.color} className="ml-1">
            {allocation.rack}
          </ColorText>
        </div>
      </div>
      <div className="col-2 d-flex justify-content-end pr-1">
        <div
          className="cursor-pointer cancel-icon"
          onClick={() => onRemove(allocation.id)}
        >
          <X size={20} />
        </div>
      </div>
    </AllocationItemContainer>
  );
}

const AllocationsContainer = styled.div`
  max-height: 40vh;
  overflow-y: auto;
  row-gap: 10px;
  display: flex;
  flex-direction: column;
  margin: 10px 0;
`;

export function ChangeLocationPopup({
  open,
  onClose,
  allocationSelection,
  setAllocationSelection,
  refresh,
  warehouseId,
}: ChangeLocationPopupProps) {
  const [selectedLocation, setSelectedLocation] = useState<string | undefined>(undefined);
  const [processing, setProcessing] = useState(false);

  const selectedAllocationIds = useMemo(() => {
    return Object.values(allocationSelection)
      .filter(x => x.selected)
      .map(x => x.id);
  }, [allocationSelection]);

  const selectedAllocation = useMemo(() => {
    return Object.values(allocationSelection).filter(x => x.selected);
  }, [allocationSelection]);

  const { data, loading } = useAllocationsLocation(
    open ? warehouseId : undefined,
    selectedAllocationIds,
  );

  const options = useMemo(() => {
    return data.map(x => ({
      text: x.code,
      key: x.locationId,
      value: x.locationId,
      available: x.available,
      content: (
        <div className="d-flex align-items-center">
          <span>{x.code}</span>
          <ColoredPill mini color={theme.colors.green} className="ml-2">
            Space available:
            <span>{x.available}</span>
            Units
          </ColoredPill>
        </div>
      ),
    }));
  }, [data]);

  const removeAllocation = (allocationId: string) => {
    setAllocationSelection(prev => {
      const newAllocationSelection = { ...prev };
      delete newAllocationSelection[allocationId];
      return newAllocationSelection;
    });
  };

  const canNewLocationAccept = useMemo(() => {
    return (
      options.find(x => x.key === selectedLocation)?.available >=
      selectedAllocation.length
    );
  }, [selectedLocation, selectedAllocation, options]);

  const changeLocation = async () => {
    if (selectedAllocation.length === 0) {
      ToastMessage.error('Please select at least one allocation');
      return;
    }

    if (!selectedLocation) {
      ToastMessage.error('Please select a new location');
      return;
    }

    try {
      setProcessing(true);
      const data = {
        moves: selectedAllocation.map(x => ({
          allocationId: x.id,
          locationId: selectedLocation,
        })),
      };
      await AllocationService.moveBulkAllocations(data);
      ToastMessage.success('Location changed successfully');
      setAllocationSelection({});
      onClose();
      refresh();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <SkupremeModal
      title={`Change Location for ${selectedAllocation?.length} Allocations`}
      open={open}
      onClose={onClose}
      size="small"
    >
      <MuteText>Select New Location</MuteText>
      <CenterDropdownStyle
        options={options}
        value={selectedLocation}
        onChange={(e, { value }) => setSelectedLocation(value as string)}
        loading={loading}
        selection
        placeholder="Select New Location"
      />
      {!canNewLocationAccept && selectedLocation ? (
        <div className="d-flex align-items-center">
          <Warning className="mr-1" size={16} color={theme.colors.red} />
          <DangerText>New location does not have enough space</DangerText>
        </div>
      ) : null}
      <div className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center my-2">
          <ItemImage
            item={{
              pImages: {
                displayImage: selectedAllocation?.[0]?.image,
              },
            }}
            size="mini"
          />
          <div className="ml-2">
            <div>{selectedAllocation?.[0]?.name}</div>
            <BoldText>{selectedAllocation?.[0]?.sku}</BoldText>
          </div>
        </div>
        <div className="d-flex align-items-center ml-2">
          <OrganisationAvatar
            name={selectedAllocation?.[0]?.customerName}
            image={selectedAllocation?.[0]?.customerImage}
            mini
          />
          <span className="ml-1">{selectedAllocation?.[0]?.customerName}</span>
        </div>
      </div>
      <AllocationsContainer>
        {selectedAllocation?.map(x => (
          <AllocationItem allocation={x} key={x.id} onRemove={removeAllocation} />
        ))}
      </AllocationsContainer>

      <div className="d-flex justify-content-end">
        <HoverBorderButton disabled={processing} onClick={onClose}>
          Close
        </HoverBorderButton>
        <StyledButton
          disabled={!canNewLocationAccept}
          className="ml-1"
          onClick={changeLocation}
          loading={processing}
        >
          Confirm
        </StyledButton>
      </div>
    </SkupremeModal>
  );
}
